import create, { UseStore } from 'zustand';
import { LocalStorage } from '@hotjar/insights-common/lib/storage-factory';
import { loadAudio } from '@hotjar/webapp/utils/audio';
import { getParameterByName } from '@hotjar/webapp/features/Playbacks/store/utils';
import { PlaybackScript } from '../../features/Playbacks/playback-script';
import { User, State, DefaultState } from './types';
import { Recording, EventMarker } from '@hotjar/webapp/features/Playbacks/types';
import { setupListeners } from './setupListeners';
import { createRecordingsListSlice } from './slices/recordingsListSlice';
import { createPlaylistSlice } from './slices/playlistSlice';
import { createConsoleSlice } from './slices/consoleSlice';

const isAutoplaying = LocalStorage.get('playbacksAutoplay');
const autostart = LocalStorage.get('playbacksAutostart');
const isScreenshotMode = Boolean(getParameterByName('forScreenshot'));
const isHighlightMode = Boolean(getParameterByName('forHighlight'));
const isLightPlayer = isScreenshotMode || isHighlightMode;
const isRetakerMode = Boolean(getParameterByName('forRetaker'));

const getOpenedCommentId = (): number | null => {
  const id = parseInt(getParameterByName('comment'), 10);

  return !isNaN(id) ? id : null;
};

let playbackScript: ReturnType<typeof PlaybackScript>;

export const initialState = (): DefaultState => ({
  // Scope Read-Only
  autostart: Boolean(typeof autostart === 'undefined' || autostart === null ? true : autostart),
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  clickSound: loadAudio(require('../../audio/click.wav'), require('../../audio/click.mp3')),
  isScreenshotMode,
  isHighlightMode,
  isLightPlayer,
  isHtmlReportMode: Boolean(getParameterByName('forHtmlReport')),
  isRetakerMode: Boolean(getParameterByName('forRetaker')),
  isCypressMode: Boolean(getParameterByName('forCypress')),
  recordingId: Number(getParameterByName('recording')),
  sharingToken: getParameterByName('token'),
  playbackScript,
  playTickInterval: 100,

  // Scope Set-Able
  debugAllEvents: false,
  debugSpecificEvents: [],
  lastTickTime: null,
  playTickCount: 0,
  playbackSpeedLogCount: { 0.5: 0, 1: 0, 2: 0, 4: 0 },
  playbackSpeedTotalLog: { 0.5: 0, 1: 0, 2: 0, 4: 0 },
  isFirstTimelineJump: true,
  timelineJumpToTime: null,
  playTimeoutId: null,
  pageToStartProcess: -1,
  currentPageIndex: 0,
  processedPosition: 0,
  currentPosition: 0,
  jumpToPause: false,
  skipNonMutationEvents: false,
  iframeStartLoadTime: null,
  nextRecordingCountdown: null,
  openWorthWatchingTooltipCountdown: null,
  hasNoIncomingFeedback: false,
  targetEndTime: null,
  filterEvents: [],
  eventMarkers: [],
  sidePanelEventMarkers: [],
  commentMentionsCalloutDisplayed: false,
  isAutoplaying: Boolean(
    typeof isAutoplaying === 'undefined' || isAutoplaying === null ? true : isAutoplaying,
  ),
  endCallback: null,
  hasLoadedFirstRecording: false,
  hasRegisteredFirstTick: false,
  playbacksOpener: '',

  // Model Read-Only
  isSharedRecording: false,
  isAutogenerated: false,
  loaded: false,
  openedCommentId: getOpenedCommentId(),
  showHotjarLogo: false,
  siteId: Number(getParameterByName('site')),
  jumpToCallback: null,
  organizationId: null,

  // Model Set-Able
  user: null,
  recording: null,
  skipPauses: Boolean(LocalStorage.get('playbacksSkipPauses')) || false,
  playbackSpeed: Number(localStorage.getItem('playbacksPlaybackSpeed') || 1),
  manuallyPausedOnce: false,
  currentPageOriginalUrl: null,
  isForcingPlayback: false,
  showLoader: true,
  done: false,
  playing: false,
  startPageContentIdentifier: null,
  availableTagNames: [],
  isWorthWatching: undefined,
  isJumpStartActive: Boolean(LocalStorage.get('playbackJumpStart', true)),
  jumpStartEvent: undefined,
  isMuteClicksActive: Boolean(LocalStorage.get('playbackMuteClicks', false)),
  showCursor: false,
  maintenanceMode: false,
  isCancelledNextRecordingCountdown: false,
  isRecordingListOutOfSync: false,
  showSidePanel:
    isLightPlayer || isRetakerMode
      ? false
      : Boolean(LocalStorage.get('playbackShowSidePanel', true)),
  showFeedbackPrompt: false,
  openWorthWatchingTooltip: false,
  isShared: false,
  showPollUpsell: false,
});

/**
 * This middleware will need to be removed once React 18 lands
 * @see https://github.com/pmndrs/zustand#calling-actions-outside-a-react-event-handler
 * @see https://reactjs.org/docs/concurrent-mode-intro.html
 */

export const createStore = (state = initialState()): UseStore<State> => {
  playbackScript = PlaybackScript();

  const store = create<State>((...fns): State => {
    const [set] = fns;

    return {
      ...state,

      // The PlaybackScript can only be instanced when the store is instanced.
      // We should rethink the approach since storing the instance in the store
      // does not make too much sense.
      playbackScript,

      setDebugEvents: (events?: Array<string>) => {
        if (Array.isArray(events)) {
          set({ debugSpecificEvents: events });
          return;
        }

        return set({ debugAllEvents: true });
      },
      setLastTickTime: (lastTickTime: number | null) => set({ lastTickTime }),
      incrementPlayTickCount: () =>
        set((state: State) => ({ playTickCount: state.playTickCount + 1 })),
      resetPlaybackSpeedLog: (playbackSpeed: number) =>
        set((state: State) => ({
          playbackSpeedLogCount: { ...state.playbackSpeedLogCount, [playbackSpeed]: 0 },
          playbackSpeedTotalLog: { ...state.playbackSpeedTotalLog, [playbackSpeed]: 0 },
        })),
      incrementPlaybackSpeedLog: (playbackSpeed: number, delta: number) =>
        set((state: State) => ({
          playbackSpeedLogCount: {
            ...state.playbackSpeedLogCount,
            [playbackSpeed]: state.playbackSpeedLogCount[playbackSpeed] + 1,
          },
          playbackSpeedTotalLog: {
            ...state.playbackSpeedTotalLog,
            [playbackSpeed]: state.playbackSpeedTotalLog[playbackSpeed] + delta,
          },
        })),
      setFirstTimelineJump: () => set({ isFirstTimelineJump: false }),
      setTimelineJumpToTime: (timelineJumpToTime: number | null) => set({ timelineJumpToTime }),
      setPlayTimeoutId: (playTimeoutId: number) => set({ playTimeoutId }),
      setPageToStartProcess: (pageToStartProcess: number) => set({ pageToStartProcess }),
      setCurrentPageIndex: (currentPageIndex: number) => set({ currentPageIndex }),
      setPosition: (currentPosition: number, processedPosition: number) =>
        set({ currentPosition, processedPosition }),
      setJumpToPause: (jumpToPause: boolean) => set({ jumpToPause }),
      setSkipNonMutationEvents: (skipNonMutationEvents: boolean) => set({ skipNonMutationEvents }),
      storeIframeLoadTime: () => set({ iframeStartLoadTime: Date.now() }),
      setNextRecordingCountdown: (nextRecordingCountdown: number) =>
        set({ nextRecordingCountdown }),
      setOpenWorthWatchingTooltipCountdown: (openWorthWatchingTooltipCountdown: number) =>
        set({ openWorthWatchingTooltipCountdown }),
      setHasNoIncomingFeedback: () => set({ hasNoIncomingFeedback: true }),
      setTargetEndTime: (targetEndTime: number) => set({ targetEndTime }),
      setEvents: (
        filterEvents: Array<EventMarker>,
        eventMarkers: Array<EventMarker>,
        sidePanelEventMarkers: Array<EventMarker>,
      ) => set({ filterEvents, eventMarkers, sidePanelEventMarkers }),
      setCommentMentionsCalloutDisplayed: () => set({ commentMentionsCalloutDisplayed: true }),
      setAutoplay: (isAutoplaying: boolean) => set({ isAutoplaying }),
      setJumpToCallback: (jumpToCallback: (() => void) | null) => set({ jumpToCallback }),
      setEndCallback: (endCallback: (() => void) | null) => set({ endCallback }),
      setHasLoadedFirstRecording: (hasLoadedFirstRecording: boolean) =>
        set({ hasLoadedFirstRecording }),
      setHasRegisteredFirstTick: (hasRegisteredFirstTick: boolean) =>
        set({ hasRegisteredFirstTick }),
      setPlaybacksOpener: (playbacksOpener: string) => set({ playbacksOpener }),
      setUser: (user: User) => set({ user }),
      setRecording: (recording: Recording) =>
        set((state: State) => ({
          recording,
          isShared: recording.shared,
          organizationId: recording.organization_id,
          isWorthWatching: recording.worth_watching,
          isSharedRecording: Boolean(state.sharingToken),
          isAutogenerated: Boolean(recording.autotags?.includes('autogenerated')),
          showHotjarLogo: Boolean(state.sharingToken),
        })),
      setSkipPauses: (skipPauses: boolean) => set({ skipPauses }),
      setLoaded: () => set({ loaded: true }),
      setPlaybackSpeed: (playbackSpeed: number) => set({ playbackSpeed }),
      setManuallyPausedOnce: (manuallyPausedOnce: boolean) => set({ manuallyPausedOnce }),
      setCurrentPageOriginalUrl: (currentPageOriginalUrl: string) =>
        set({ currentPageOriginalUrl }),
      setIsForcingPlayback: (isForcingPlayback: boolean) => set({ isForcingPlayback }),
      setShowLoader: (showLoader: boolean) => set({ showLoader }),
      setDone: (done: boolean) => set({ done }),
      setPlaying: (playing: boolean) => set({ playing }),
      setStartPageContentIdentifier: (startPageContentIdentifier: string) =>
        set({ startPageContentIdentifier }),
      setAvailableTagNames: (availableTagNames: Array<string>) => set({ availableTagNames }),
      setWorthWatching: (isWorthWatching: boolean) => set({ isWorthWatching }),
      setIsJumpStartActive: (isJumpStartActive: boolean) => set({ isJumpStartActive }),
      setJumpStartEvent: (jumpStartEvent: EventMarker) => set({ jumpStartEvent }),
      setIsMuteClicksActive: (isMuteClicksActive: boolean) => set({ isMuteClicksActive }),
      setShowCursor: (showCursor: boolean) => set({ showCursor }),
      setEnableMaintenanceMode: () => set({ maintenanceMode: true }),
      setCancelledNextRecordingCountdown: (isCancelledNextRecordingCountdown: boolean) =>
        set({ isCancelledNextRecordingCountdown }),
      setIsRecordingListOutOfSync: (isRecordingListOutOfSync: boolean) =>
        set({ isRecordingListOutOfSync }),
      setShowSidePanel: (showSidePanel: boolean) => set({ showSidePanel }),
      setShowFeedbackPrompt: (showFeedbackPrompt: boolean) => set({ showFeedbackPrompt }),
      setOpenWorthWatchingTooltip: (openWorthWatchingTooltip: boolean) =>
        set({ openWorthWatchingTooltip }),
      setShared: (isShared: boolean) => set({ isShared }),
      setShowPollUpsell: (showPollUpsell: boolean) => set({ showPollUpsell }),
      reset: ({
        hasLoadedFirstRecording,
        hasRegisteredFirstTick,
        playlistVisible,
        playlistOpened,
        lastFetchedRecordingId,
      }: State) =>
        set({
          ...initialState(),
          hasLoadedFirstRecording,
          hasRegisteredFirstTick,
          playlistVisible,
          playlistOpened,
          lastFetchedRecordingId,
        }),

      ...createRecordingsListSlice(...fns),
      ...createPlaylistSlice(...fns),
      ...createConsoleSlice(...fns),
    };
  });

  // Setup store listeners
  setupListeners(store);

  return store;
};
